import { Helmet } from 'react-helmet-async';

const defaultMeta = {
  siteName: 'Artisan Market & Studio',
  siteUrl: 'https://artisanmarketandstudio.com',
  defaultTitle: 'Artisan Market & Studio | Craft Show Organizers in DC Metro Area',
  defaultDescription: 'Award-winning craft show organizers serving DC, Virginia, Maryland, West Virginia, Delaware & Pennsylvania. Vendor opportunities, artisan events, and business education for creative entrepreneurs.',
  defaultImage: 'https://customer-assets.emergentagent.com/job_craft-collective-11/artifacts/cpg0ir3f_AMS%20Logo.jpg',
  twitterHandle: '@artisanmarketva',
};

const pageSEO = {
  home: {
    title: 'Artisan Market & Studio | Craft Shows & Vendor Opportunities in DC Metro Area',
    description: 'Join 100+ artisans at AMS - award-winning craft show organizers serving Virginia, Maryland, DC, West Virginia, Delaware & Pennsylvania. Vendor opportunities, events, and business education for creative entrepreneurs.',
    keywords: 'craft show organizers, vendor opportunities, artisan market DC, craft fairs Virginia, handmade markets Maryland, artisan events DMV, craft show vendors, maker markets Mid-Atlantic',
  },
  events: {
    title: 'AMS Events | Craft Shows & Vendor Opportunities in Virginia, Maryland & DC',
    description: 'Looking for craft show vendor opportunities? AMS Events organizes artisan markets throughout DC, Virginia, Maryland, West Virginia, Delaware & Pennsylvania. Book us for your venue or become a vendor today!',
    keywords: 'craft show vendor opportunities, artisan market events, craft fairs DC metro, vendor booth rental, craft show organizers Virginia, Maryland craft shows, handmade markets Pennsylvania, artisan events West Virginia',
  },
  university: {
    title: 'AMS University | Business Education for Artisans & Craft Vendors',
    description: 'Learn business skills designed for creative entrepreneurs. AMS University offers online courses and in-person workshops on pricing, marketing, and running a successful artisan business in the Mid-Atlantic region.',
    keywords: 'artisan business education, craft vendor training, pricing handmade goods, craft show success, artisan marketing courses, creative entrepreneur workshops, maker business skills',
  },
  compass: {
    title: 'AMS Compass | Business Tools for Craft Show Vendors & Artisans',
    description: 'Your artisan business copilot. Track sales, find craft shows, and manage your creative business with AI-powered tools designed for vendors in DC, Virginia, Maryland & the Mid-Atlantic.',
    keywords: 'artisan business tools, craft show finder, vendor sales tracking, craft fair calendar, artisan event management, handmade business software',
  },
  vendors: {
    title: 'Become a Vendor | Craft Show Opportunities in DC, VA, MD, WV, DE & PA',
    description: 'Join our award-winning artisan collective! Vendor opportunities at premium craft shows throughout Washington DC, Virginia, Maryland, West Virginia, Delaware & Pennsylvania. Apply today!',
    keywords: 'craft show vendor application, artisan vendor opportunities, sell at craft fairs, handmade vendor booth, craft market vendors DC, Virginia artisan markets, Maryland craft show vendors',
  },
  gallery: {
    title: 'Artisan Gallery | Handmade Crafts from DC Metro Area Artists',
    description: 'Explore beautiful handcrafted goods from our talented artisan community in Virginia, Maryland, and the DC metro area. Pottery, jewelry, textiles, woodwork, and more.',
    keywords: 'handmade crafts gallery, artisan products DC, handcrafted goods Virginia, local artists Maryland, artisan jewelry, handmade pottery, craft market gallery',
  },
  contact: {
    title: 'Contact AMS | Craft Show Organizers & Vendor Inquiries',
    description: 'Contact Artisan Market & Studio for vendor opportunities, event bookings, or partnership inquiries. Serving DC, Virginia, Maryland, West Virginia, Delaware & Pennsylvania.',
    keywords: 'contact craft show organizers, vendor inquiry, book craft show, artisan market contact, event organizers DC metro',
  },
  festivalOfTrees: {
    title: 'Festival of Trees at the Barn | Holiday Craft Market Ashburn VA',
    description: 'Join us November 20-21, 2025 at The Barn at Brambleton for the Festival of Trees! Vendor opportunities available for this premier holiday craft market in Ashburn, Virginia.',
    keywords: 'Festival of Trees Ashburn, holiday craft market Virginia, Barn at Brambleton events, Christmas craft show Loudoun, holiday vendor opportunities, artisan holiday market DC metro',
  },
};

export function SEOHead({ page = 'home', customTitle, customDescription }) {
  const seo = pageSEO[page] || pageSEO.home;
  const title = customTitle || seo.title;
  const description = customDescription || seo.description;

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Artisan Market & Studio',
    alternateName: 'AMS',
    url: defaultMeta.siteUrl,
    logo: defaultMeta.defaultImage,
    description: 'Award-winning craft show organizers and artisan collective serving the DC metro area and Mid-Atlantic region.',
    address: {
      '@type': 'PostalAddress',
      addressRegion: 'VA',
      addressCountry: 'US',
    },
    areaServed: [
      { '@type': 'State', name: 'Virginia' },
      { '@type': 'State', name: 'Maryland' },
      { '@type': 'State', name: 'West Virginia' },
      { '@type': 'State', name: 'Delaware' },
      { '@type': 'State', name: 'Pennsylvania' },
      { '@type': 'City', name: 'Washington, DC' },
    ],
    sameAs: [
      'https://facebook.com/artisanmarketandstudio',
      'https://instagram.com/artisanmarketandstudio',
      'https://pinterest.com/artisanmarketandstudio',
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'info@artisanmarketandstudio.com',
      contactType: 'customer service',
    },
  };

  const localBusinessData = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': defaultMeta.siteUrl,
    name: 'Artisan Market & Studio',
    image: defaultMeta.defaultImage,
    description: 'Craft show organizers providing vendor opportunities throughout the DC metro area, Virginia, Maryland, West Virginia, Delaware, and Pennsylvania.',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Loudoun County',
      addressRegion: 'VA',
      addressCountry: 'US',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 39.0568,
      longitude: -77.4636,
    },
    areaServed: {
      '@type': 'GeoCircle',
      geoMidpoint: {
        '@type': 'GeoCoordinates',
        latitude: 38.9072,
        longitude: -77.0369,
      },
      geoRadius: '200 mi',
    },
    priceRange: '$$',
    openingHours: 'Mo-Fr 09:00-17:00',
    email: 'info@artisanmarketandstudio.com',
    award: 'Best of Loudoun 2024 - Gift Store',
  };

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={seo.keywords} />
      
      {/* Geographic Meta Tags */}
      <meta name="geo.region" content="US-VA" />
      <meta name="geo.placename" content="Loudoun County, Virginia" />
      <meta name="geo.position" content="39.0568;-77.4636" />
      <meta name="ICBM" content="39.0568, -77.4636" />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={defaultMeta.siteUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={defaultMeta.defaultImage} />
      <meta property="og:site_name" content={defaultMeta.siteName} />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={defaultMeta.siteUrl} />
      <meta property="twitter:title" content={title} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={defaultMeta.defaultImage} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={defaultMeta.siteUrl} />
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(localBusinessData)}
      </script>
    </Helmet>
  );
}

export { pageSEO, defaultMeta };
